import statesJson from '@/src/lib/utils/states.json';
import { routing } from '@/src/i18n/routing';
import type {
  HtmlSitemapArea,
  HtmlSitemapAreaType,
  HtmlSitemapPropertyTypeGroup,
  HtmlSitemapResponse,
} from '@/src/lib/api';

// Property-type slugs the sitemap renders. Drives PHRASE_MAP keys + section config
// and matches the BE `searchSlug` values returned by the V3 htmlSitemap endpoint.
type HtmlSitemapHeader =
  | 'apartments-for-rent'
  | 'houses-for-rent'
  | 'condos-for-rent'
  | 'affordable-apartments-for-rent'
  | 'for-rent-by-owner'
  | 'rooms-for-rent'
  | 'sublets'
  | 'student-housing';

export type StateEntry = {
  id?: string | number;
  name: string;
  abbreviation: string;
  topCities: Array<string>;
  resourceId: string;
};

export type StateSitemapArea = {
  id: string;
  resourceId: string | null;
  name: string;
  uriV2: string;
  listingCount?: number;
  phrase: string;
};

export type StateSitemapGroup = {
  key: string;
  title: string;
  areas: Array<StateSitemapArea>;
};

export type StateSitemapSection = {
  key: string;
  title: string;
  header?: HtmlSitemapHeader;
  groups: Array<StateSitemapGroup>;
};

export type MultiFamilyBuildingListing = {
  displayName?: string | null;
  name?: string | null;
  title?: string | null;
  uri?: string | null;
  uriV2?: string | null;
  uriMalone?: string | null;
  city?: string | null;
  address?: {
    city?: string | null;
  } | null;
  listings?: Array<{
    displayName?: string | null;
    name?: string | null;
    title?: string | null;
    uri?: string | null;
    uriV2?: string | null;
    uriMalone?: string | null;
    address?: {
      city?: string | null;
    } | null;
  }> | null;
};

export type CitySitemapPathData = {
  state: StateEntry;
  stateResourceId: string;
  citySlug: string;
  cityName: string;
  stateName: string;
  stateAbbreviation: string;
  resourceId: string;
};

type PropertyTypeConfig = {
  key: string;
  title: string;
  header: HtmlSitemapHeader;
  searchSlug: string;
  fallbackPhrase: string;
};

type PhraseMap = Record<HtmlSitemapHeader, { singular: string; plural: string }>;
type AreaTypeGroupKeys = Partial<Record<HtmlSitemapAreaType, string>>;

const states = statesJson as Array<StateEntry>;

export const STATE_PAGE_STATES = states;
export const STATE_PAGE_RESOURCE_IDS = new Set(states.map((state) => state.resourceId));
export const MULTI_FAMILY_BUILDINGS_SECTION_KEY = 'multi-family-buildings';

export const CITY_SITEMAP_ENABLED_STATES = new Set([
  'AL',
  'AZ',
  'CA',
  'CO',
  'CT',
  'HI',
  'IL',
  'IN',
  'KY',
  'MA',
  'MD',
  'MI',
  'MN',
  'MO',
  'NC',
  'NH',
  'NJ',
  'OH',
  'OR',
  'PA',
  'SC',
  'TN',
  'VT',
  'WI',
  'WV',
]);

export const STATE_PAGE_REVALIDATE_SECONDS = 86400;

const PROPERTY_TYPE_CONFIGS: Array<PropertyTypeConfig> = [
  {
    key: 'apartments',
    title: 'Apartments',
    header: 'apartments-for-rent',
    searchSlug: 'apartments-for-rent',
    fallbackPhrase: 'Apartments for rent',
  },
  {
    key: 'houses',
    title: 'Houses',
    header: 'houses-for-rent',
    searchSlug: 'houses-for-rent',
    fallbackPhrase: 'Houses for rent',
  },
  {
    key: 'condos',
    title: 'Condos',
    header: 'condos-for-rent',
    searchSlug: 'condos-for-rent',
    fallbackPhrase: 'Condos for rent',
  },
  {
    key: 'affordable-apartments',
    title: 'Affordable apartments',
    header: 'affordable-apartments-for-rent',
    searchSlug: 'affordable-apartments-for-rent',
    fallbackPhrase: 'Affordable apartments for rent',
  },
  {
    key: 'for-rent-by-owner',
    title: 'For rent by owner',
    header: 'for-rent-by-owner',
    searchSlug: 'for-rent-by-owner',
    fallbackPhrase: 'Listings for rent by owner',
  },
  {
    key: 'rooms-for-rent',
    title: 'Rooms for rent',
    header: 'rooms-for-rent',
    searchSlug: 'rooms-for-rent',
    fallbackPhrase: 'Rooms for rent',
  },
  {
    key: 'sublets',
    title: 'Sublets',
    header: 'sublets',
    searchSlug: 'sublets',
    fallbackPhrase: 'Sublets',
  },
];

const AREA_TYPE_GROUP_KEYS = {
  city: 'cities',
  zip: 'zip-codes',
  county: 'counties',
  neighborhood: 'neighborhoods',
} as const;

// City pages omit the "cities" group (a city's only sub-city is itself).
// Area-group display labels now come from the BE response (`displayName`).
const CITY_AREA_TYPE_GROUP_KEYS = {
  zip: 'zip-codes',
  county: 'counties',
  neighborhood: 'neighborhoods',
} as const;

const PHRASE_MAP: PhraseMap = {
  'apartments-for-rent': {
    singular: 'apartment for rent',
    plural: 'apartments for rent',
  },
  'houses-for-rent': {
    singular: 'house for rent',
    plural: 'houses for rent',
  },
  'condos-for-rent': {
    singular: 'condo for rent',
    plural: 'condos for rent',
  },
  'affordable-apartments-for-rent': {
    singular: 'affordable apartment for rent',
    plural: 'affordable apartments for rent',
  },
  'for-rent-by-owner': {
    singular: 'listing for rent by owner',
    plural: 'listings for rent by owner',
  },
  'rooms-for-rent': {
    singular: 'room for rent',
    plural: 'rooms for rent',
  },
  sublets: {
    singular: 'sublet',
    plural: 'sublets',
  },
  'student-housing': {
    singular: 'student housing and apartments',
    plural: 'student housing and apartments',
  },
};

export function findStateByResourceId(resourceId: string): StateEntry | undefined {
  return states.find((state) => state.resourceId === resourceId);
}

export function getOtherStates(currentState: StateEntry): Array<StateEntry> {
  return states.filter((state) => state.resourceId !== currentState.resourceId);
}

export function isStatePageResourceId(resourceId: string): boolean {
  return STATE_PAGE_RESOURCE_IDS.has(resourceId);
}

/**
 * Whether a request pathname (as reported by the middleware pathname header)
 * is a pure SEO surface — currently the single-segment state hubs. These pages
 * carry no ads or conversion behavior, so the root layout drops the marketing
 * pixel set on them (HP-8781). City sitemap URLs are excluded on purpose:
 * a two-segment /{state}/{slug} can resolve to a live SRP.
 */
export function isSeoOnlyPathname(pathname: string | null): boolean {
  if (!pathname || !pathname.startsWith('/')) return false;

  const segments = pathname.split('/').filter(Boolean);
  const routeSegments = routing.locales.includes(segments[0] as (typeof routing.locales)[number])
    ? segments.slice(1)
    : segments;

  return routeSegments.length === 1 && isStatePageResourceId(routeSegments[0]);
}

export function getCanonicalStatePath(state: StateEntry): string {
  return `/${state.resourceId}`;
}

export function getCanonicalCityPath(data: CitySitemapPathData): string {
  return `/${data.state.resourceId}/${data.citySlug}`;
}

export function getStateSearchHref(state: StateEntry): string {
  return `/${state.abbreviation.toLowerCase()}/apartments-for-rent`;
}

export function getCityResourceId(citySlug: string, state: StateEntry): string {
  return `${citySlug}-${state.abbreviation.toLowerCase()}`;
}

export function getCitySearchHref(data: CitySitemapPathData): string {
  return `/${data.resourceId}/apartments-for-rent`;
}

export function isCitySitemapEnabled(stateAbbreviation: string): boolean {
  return CITY_SITEMAP_ENABLED_STATES.has(stateAbbreviation.toUpperCase());
}

export function dashToTitleCase(slug: string): string {
  return slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function parseCitySitemapSegments(
  stateResourceId: string,
  citySlug: string,
): CitySitemapPathData | null {
  const state = findStateByResourceId(stateResourceId);

  if (!state || !isCitySitemapEnabled(state.abbreviation) || !isValidCitySlug(citySlug)) {
    return null;
  }

  return {
    state,
    stateResourceId,
    citySlug,
    cityName: dashToTitleCase(citySlug),
    stateName: state.name,
    stateAbbreviation: state.abbreviation,
    resourceId: getCityResourceId(citySlug, state),
  };
}

export function isCitySitemapRoute(stateResourceId: string, citySlug: string): boolean {
  return Boolean(parseCitySitemapSegments(stateResourceId, citySlug));
}

export function getCitySitemapHref(area: StateSitemapArea, state: StateEntry): string | null {
  if (!isCitySitemapEnabled(state.abbreviation)) {
    return null;
  }

  const resourceId = area.resourceId ?? extractResourceIdFromUriV2(area.uriV2);
  const stateSuffix = `-${state.abbreviation.toLowerCase()}`;

  if (!resourceId?.endsWith(stateSuffix)) {
    return null;
  }

  const citySlug = resourceId.slice(0, -stateSuffix.length);
  return citySlug ? `/${state.resourceId}/${citySlug}` : null;
}

export function buildStateSitemapSections(
  sitemap: HtmlSitemapResponse | undefined,
  fallbackState?: StateEntry,
): Array<StateSitemapSection> {
  const sections = buildSectionsFromPropertyTypes(sitemap?.propertyTypes, AREA_TYPE_GROUP_KEYS);

  if (sections.length > 0) {
    return sections;
  }

  return fallbackState ? buildFallbackStateSitemapSections(fallbackState) : [];
}

export function buildCitySitemapSections(
  sitemap: HtmlSitemapResponse | undefined,
  fallbackData?: CitySitemapPathData,
): Array<StateSitemapSection> {
  const sections = buildSectionsFromPropertyTypes(sitemap?.propertyTypes, CITY_AREA_TYPE_GROUP_KEYS);

  if (sections.length > 0) {
    return sections;
  }

  return fallbackData ? buildFallbackCitySitemapSections(fallbackData) : [];
}

export function buildMultiFamilyBuildingsSection(
  listings: Array<MultiFamilyBuildingListing> | undefined,
): StateSitemapSection | null {
  if (!Array.isArray(listings) || listings.length === 0) {
    return null;
  }

  const areas = listings
    .map((listing, index) => {
      const firstUnit = listing.listings?.[0];
      const name =
        listing.displayName ??
        listing.name ??
        listing.title ??
        firstUnit?.displayName ??
        firstUnit?.name ??
        firstUnit?.title;
      const buildingUri =
        listing.uriV2 ??
        listing.uri ??
        listing.uriMalone;
      const listingUri = firstUnit?.uriMalone ?? firstUnit?.uriV2 ?? firstUnit?.uri;
      const uri = listing.listings?.length === 1 ? listingUri ?? buildingUri : buildingUri ?? listingUri;
      const city = listing.address?.city ?? listing.city ?? firstUnit?.address?.city;

      if (!name || !uri) {
        return null;
      }

      return {
        id: `multi-family-building-${index}-${uri}`,
        resourceId: extractResourceIdFromUriV2(uri),
        name,
        uriV2: uri,
        phrase: city ? `Building in ${city}` : 'Apartment building',
      } satisfies StateSitemapArea;
    })
    .filter((area): area is StateSitemapArea => area !== null);

  if (areas.length === 0) {
    return null;
  }

  return {
    key: MULTI_FAMILY_BUILDINGS_SECTION_KEY,
    title: 'Multi-family buildings',
    groups: [
      {
        key: MULTI_FAMILY_BUILDINGS_SECTION_KEY,
        title: 'Popular multi-family buildings',
        areas,
      },
    ],
  };
}

export function appendMultiFamilyBuildingsSection(
  sections: Array<StateSitemapSection>,
  listings: Array<MultiFamilyBuildingListing> | undefined,
): Array<StateSitemapSection> {
  const multiFamilyBuildingsSection = buildMultiFamilyBuildingsSection(listings);
  return multiFamilyBuildingsSection ? [...sections, multiFamilyBuildingsSection] : sections;
}

export function buildFallbackStateSitemapSections(state: StateEntry): Array<StateSitemapSection> {
  return PROPERTY_TYPE_CONFIGS.filter((config) => config.header !== 'sublets').map((config) => ({
    key: config.key,
    title: config.title,
    header: config.header,
    groups: [
      {
        key: 'cities',
        title: 'Popular cities',
        areas: state.topCities.map((citySlug) => {
          const cityName = dashToTitleCase(citySlug);
          const resourceId = `${citySlug}-${state.abbreviation.toLowerCase()}`;

          return {
            id: resourceId,
            resourceId,
            name: cityName,
            uriV2: `/${resourceId}/${config.searchSlug}`,
            phrase: config.fallbackPhrase,
          };
        }),
      },
    ],
  }));
}

export function buildFallbackCitySitemapSections(data: CitySitemapPathData): Array<StateSitemapSection> {
  return PROPERTY_TYPE_CONFIGS.filter((config) => config.header !== 'sublets').map((config) => ({
    key: config.key,
    title: config.title,
    header: config.header,
    groups: [
      {
        key: 'searches',
        title: 'Popular searches',
        areas: [
          {
            id: `${data.resourceId}-${config.searchSlug}`,
            resourceId: data.resourceId,
            name: `${data.cityName} ${config.fallbackPhrase}`,
            uriV2: `/${data.resourceId}/${config.searchSlug}`,
            phrase: `${config.fallbackPhrase} in ${data.stateName}`,
          },
        ],
      },
    ],
  }));
}

function buildSectionsFromPropertyTypes(
  propertyTypes: Array<HtmlSitemapPropertyTypeGroup> | undefined,
  areaTypeGroupKeys: AreaTypeGroupKeys,
): Array<StateSitemapSection> {
  if (!Array.isArray(propertyTypes)) {
    return [];
  }

  return PROPERTY_TYPE_CONFIGS.map((config): StateSitemapSection | null => {
    const propertyType = propertyTypes.find((entry) => entry.searchSlug === config.searchSlug);
    if (!propertyType) {
      return null;
    }

    const groups = buildGroupsForPropertyType(propertyType, config.header, areaTypeGroupKeys);
    if (groups.length === 0) {
      return null;
    }

    return {
      key: config.key,
      // Label is BE-owned (thin client); config.title is only the static fallback label.
      title: propertyType.displayName,
      header: config.header,
      groups,
    };
  }).filter((section): section is StateSitemapSection => section !== null);
}

function buildGroupsForPropertyType(
  propertyType: HtmlSitemapPropertyTypeGroup,
  header: HtmlSitemapHeader,
  areaTypeGroupKeys: AreaTypeGroupKeys,
): Array<StateSitemapGroup> {
  return propertyType.areaGroups
    .map((areaGroup) => {
      const groupKey = areaTypeGroupKeys[areaGroup.areaType];
      if (!groupKey) {
        return null;
      }

      const areas = mapAreas(areaGroup.areas, header);
      if (areas.length === 0) {
        return null;
      }

      return {
        key: groupKey,
        title: `Popular ${areaGroup.displayName}`,
        areas,
      } satisfies StateSitemapGroup;
    })
    .filter((group): group is StateSitemapGroup => group !== null);
}

function mapAreas(areas: Array<HtmlSitemapArea>, header: HtmlSitemapHeader): Array<StateSitemapArea> {
  return areas
    .filter((area) => area.name.length > 0 && area.uri.length > 0)
    .map((area, index) => {
      const resourceId = extractResourceIdFromUriV2(area.uri);

      return {
        id: resourceId ?? `${area.name}-${index}`,
        resourceId,
        name: area.name,
        uriV2: area.uri,
        listingCount: area.listingCount,
        phrase: `${area.listingCount} ${getPhrase(area.listingCount, header)}`,
      } satisfies StateSitemapArea;
    });
}

function extractResourceIdFromUriV2(uriV2: string | undefined): string | null {
  if (!uriV2) {
    return null;
  }

  const [, resourceId] = uriV2.split('/');
  return resourceId || null;
}

function isValidCitySlug(citySlug: string): boolean {
  return citySlug.length >= 2 && citySlug.length <= 50 && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(citySlug);
}

function getPhrase(listingCount: number, header: HtmlSitemapHeader): string {
  const phrase = PHRASE_MAP[header];
  return phrase[listingCount === 1 ? 'singular' : 'plural'];
}
